import type { ReactNode } from 'react';
import { ChevronLeft } from 'lucide-react';
import { Button } from './button';
import { cn } from '@/lib/utils';

export interface InlineViewProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onBack?: () => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  actions?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

/**
 * Vista de trabajo dentro del cuerpo de la página. Sustituye a los modales:
 * ocupa el ancho disponible y deja la pantalla libre para formularios y tablas.
 */
export function InlineView({ open = true, onOpenChange, onBack, title, description, children, footer, actions, className }: InlineViewProps) {
  if (!open) return null;
  const back = onBack ?? (() => onOpenChange?.(false));
  return (
    <section className={cn('panel animate-fade-in', className)}>
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border bg-card px-4 py-3">
        <div className="flex min-w-0 items-start gap-2">
          <Button variant="ghost" size="icon-sm" onClick={back} aria-label="Volver">
            <ChevronLeft />
          </Button>
          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold text-foreground">{title}</h2>
            {description ? <p className="mt-0.5 text-xs text-muted-foreground">{description}</p> : null}
          </div>
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
      <div className="p-4 lg:p-6">{children}</div>
      {footer ? (
        <div className="sticky bottom-0 flex flex-wrap items-center justify-end gap-2 border-t border-border bg-card/95 px-4 py-3 backdrop-blur">
          {footer}
        </div>
      ) : null}
    </section>
  );
}
